import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type } from "arktype";
import { Trash2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OverlayShell } from "@/components/ui/overlay";
import { format } from "@/lib/format";

const DAYS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const TYPES = ["Consulta", "Retorno", "Cirurgia", "Procedimento", "Bloqueio"] as const;
const clock = type("string").matching(/^([01]\d|2[0-3]):[0-5]\d$/u);
const weeklyForm = type({
	weekday: "0 <= number.integer <= 6",
	start_time: clock,
	end_time: clock,
}).narrow((value, ctx) => value.start_time < value.end_time || ctx.mustBe("an ascending window"));
const durationForm = type({
	tipo: type.enumerated(...TYPES),
	duration_minutes: "5 <= number.integer <= 720",
});
const exceptionForm = type({
	date: "string",
	start_time: clock,
	end_time: clock,
	kind: type.enumerated("available", "unavailable"),
}).narrow((value, ctx) => value.start_time < value.end_time || ctx.mustBe("an ascending window"));

export function AgendaSettingsModal({ onClose }: { onClose: () => void }) {
	const queryClient = useQueryClient();
	const { data } = useQuery(orpc.agenda.availability.queryOptions());
	const refresh = async () => {
		await queryClient.invalidateQueries({ queryKey: orpc.agenda.availability.key() });
	};
	const weekly = useMutation(
		orpc.agenda.setWeeklyWindow.mutationOptions({
			onSuccess: refresh,
			onError: () => toast.error("Não foi possível alterar a jornada"),
		}),
	);
	const duration = useMutation(
		orpc.agenda.setDuration.mutationOptions({
			onSuccess: refresh,
			onError: () => toast.error("Não foi possível alterar a duração"),
		}),
	);
	const exception = useMutation(
		orpc.agenda.addException.mutationOptions({
			onSuccess: refresh,
			onError: () => toast.error("Não foi possível adicionar a exceção"),
		}),
	);
	const removeException = useMutation(
		orpc.agenda.removeException.mutationOptions({
			onSuccess: refresh,
			onError: () => toast.error("Não foi possível remover a exceção"),
		}),
	);
	const weeklyFields = useForm<typeof weeklyForm.infer>({
		resolver: arktypeResolver(weeklyForm),
		defaultValues: { weekday: 1, start_time: "08:00", end_time: "18:00" },
	});
	const durationFields = useForm<typeof durationForm.infer>({
		resolver: arktypeResolver(durationForm),
		defaultValues: { tipo: "Consulta", duration_minutes: 60 },
	});
	const exceptionFields = useForm<typeof exceptionForm.infer>({
		resolver: arktypeResolver(exceptionForm),
		defaultValues: {
			date: format.today(),
			start_time: "08:00",
			end_time: "18:00",
			kind: "unavailable",
		},
	});

	return (
		<OverlayShell
			label="Configurar agenda"
			onClose={onClose}
			className="items-center justify-center p-4"
		>
			<div className="overlay-panel max-w-2xl gap-5 p-5">
				<div className="flex items-center justify-between gap-3">
					<h2 className="type-section-title">Configurar agenda</h2>
					<Button variant="ghost" onClick={onClose}>
						Fechar
					</Button>
				</div>

				<section className="flex flex-col gap-3">
					<h3 className="text-sm font-semibold">Jornada semanal</h3>
					<div className="divide-y divide-border rounded-lg border border-border">
						{data?.weekly.map((window) => (
							<div key={window.id} className="flex items-center gap-2 px-3 py-2 text-sm">
								<span className="min-w-24">{DAYS[window.weekday]}</span>
								<span className="font-mono text-xs">
									{window.start_time} a {window.end_time}
								</span>
								<Button
									className="ml-auto"
									size="icon"
									variant="ghost"
									aria-label="Remover janela"
									onClick={() =>
										weekly.mutate({
											weekday: window.weekday,
											windows: data.weekly
												.filter((item) => item.weekday === window.weekday && item.id !== window.id)
												.map((item) => ({
													start_time: item.start_time,
													end_time: item.end_time,
												})),
										})
									}
								>
									<Trash2 />
								</Button>
							</div>
						))}
						{data?.weekly.length === 0 && (
							<p className="px-3 py-4 text-sm text-muted-foreground">
								Jornada ainda não configurada.
							</p>
						)}
					</div>
					<form
						onSubmit={weeklyFields.handleSubmit((value) => {
							const current =
								data?.weekly
									.filter((window) => window.weekday === value.weekday)
									.map((window) => ({
										start_time: window.start_time,
										end_time: window.end_time,
									})) ?? [];

							weekly.mutate({
								weekday: value.weekday,
								windows: [...current, { start_time: value.start_time, end_time: value.end_time }],
							});
						})}
						className="grid grid-cols-2 gap-2 sm:grid-cols-4"
					>
						<select
							{...weeklyFields.register("weekday", { valueAsNumber: true })}
							aria-label="Dia da semana"
							className="control-field h-11 md:h-10"
						>
							{DAYS.map((day, index) => (
								<option key={day} value={index}>
									{day}
								</option>
							))}
						</select>
						<Input
							{...weeklyFields.register("start_time")}
							aria-label="Início da jornada"
							type="time"
						/>
						<Input {...weeklyFields.register("end_time")} aria-label="Fim da jornada" type="time" />
						<Button type="submit" disabled={weekly.isPending}>
							Adicionar
						</Button>
					</form>
				</section>

				<section className="flex flex-col gap-3">
					<h3 className="text-sm font-semibold">Duração padrão</h3>
					<form
						onSubmit={durationFields.handleSubmit((value) => duration.mutate(value))}
						className="grid grid-cols-3 gap-2"
					>
						<select
							{...durationFields.register("tipo")}
							aria-label="Tipo de atendimento"
							className="col-span-1 control-field h-11 md:h-10"
						>
							{TYPES.map((tipo) => (
								<option key={tipo}>{tipo}</option>
							))}
						</select>
						<Input
							{...durationFields.register("duration_minutes", { valueAsNumber: true })}
							aria-label="Duração em minutos"
							type="number"
							min={5}
							step={5}
						/>
						<Button type="submit" disabled={duration.isPending}>
							Salvar
						</Button>
					</form>
				</section>

				<section className="flex flex-col gap-3">
					<h3 className="text-sm font-semibold">Exceções</h3>
					<div className="divide-y divide-border rounded-lg border border-border">
						{data?.exceptions.map((item) => (
							<div key={item.id} className="flex items-center gap-2 px-3 py-2 text-sm">
								<span>{format.date(item.date)}</span>
								<span className="font-mono text-xs">
									{item.start_time} a {item.end_time}
								</span>
								<span className="text-xs text-muted-foreground">
									{item.kind === "available" && "Disponível"}
									{item.kind === "unavailable" && "Indisponível"}
								</span>
								<Button
									className="ml-auto"
									size="icon"
									variant="ghost"
									aria-label="Remover exceção"
									onClick={() => removeException.mutate({ id: item.id })}
								>
									<Trash2 />
								</Button>
							</div>
						))}
					</div>
					<form
						onSubmit={exceptionFields.handleSubmit((value) => exception.mutate(value))}
						className="grid grid-cols-2 gap-2 sm:grid-cols-5"
					>
						<Input {...exceptionFields.register("date")} aria-label="Data da exceção" type="date" />
						<Input
							{...exceptionFields.register("start_time")}
							aria-label="Início da exceção"
							type="time"
						/>
						<Input
							{...exceptionFields.register("end_time")}
							aria-label="Fim da exceção"
							type="time"
						/>
						<select
							{...exceptionFields.register("kind")}
							aria-label="Tipo de exceção"
							className="control-field h-11 md:h-10"
						>
							<option value="unavailable">Indisponível</option>
							<option value="available">Disponível</option>
						</select>
						<Button type="submit" disabled={exception.isPending}>
							Adicionar
						</Button>
					</form>
				</section>
			</div>
		</OverlayShell>
	);
}
